import type { JSX } from 'react';
import { Cast, Eye, Map as MapIcon, Play } from 'lucide-react';
import { useSessionContext } from '../../contexts/SessionContext';
import { useI18n } from '../../i18n';
import { StripShowConfirm, useMapStripShow } from '../MapViewStatus';
import { Hint, IconButton } from '../ui/icon-button';
import { RowMenu } from '../ui/row-menu';

/** A hint of two lines (what it is, then what it means). */
const lines = (text: string): JSX.Element => <span className="whitespace-pre-line">{text}</span>;

export interface StudioMapStripProps {
  /** The maps open in the Studio panes (the main pane first), in view now. Empty — no map open. */
  readonly viewingPageIds: readonly string[];
  /** Local only: the players' map as a tab of the workspace. Never activates anything. */
  readonly onOpenPlayersMap: () => void;
}

/**
 * Which map the GM has open and which the players see — ONCE for the whole Studio, one compact line (owner 30.10:
 * «компактное отображение … сейчас 2 раза может быть показано на 2х картах»). The words «У тебя» / «У игроков» are
 * icons with hints (and words for a screen reader), the names stay; «Открыть карту игроков» and «Показать игрокам…»
 * are icon buttons. Two maps open — both are named, «Показать игрокам…» asks which. The show is confirmed (the same
 * confirmation as the table's strip); the table keeps its own strip (MapViewStatus).
 */
export function StudioMapStrip({ viewingPageIds, onOpenPlayersMap }: StudioMapStripProps): JSX.Element | null {
  const { mp, pages, isGM } = useSessionContext();
  const { t } = useI18n();
  const { nameOf, flow } = useMapStripShow();
  if (!mp.session || !isGM) return null;

  const active = pages.activePageId;
  const activeName = nameOf(active) ?? '…';
  const viewing = viewingPageIds.filter((id, i) => viewingPageIds.indexOf(id) === i && nameOf(id) !== undefined);
  const together = viewing.length > 0 && viewing.every((id) => id === active);
  // What can be shown: an open map the players are not on.
  const showable = viewing.filter((id) => id !== active);
  const pending = flow.attempt?.pending === true;
  const note = flow.attempt !== undefined && !flow.attempt.pending && viewing.includes(flow.attempt.pageId) ? flow.attempt.note : undefined;
  const youWords = t('mapView.youSee').split('{name}')[0] ?? '';
  const playersWords = t('mapView.playersSee').split('{name}')[0] ?? '';
  const togetherWords = t('mapView.together').split('{name}')[0] ?? '';
  const ownHint = t('mapView.ownViewHint').replace('{name}', activeName);
  const showLabel = pending ? t('mapView.showing') : t('mapView.show');

  return (
    <div
      className="studio-map-strip flex h-7 min-w-0 items-center gap-2 text-meta"
      data-testid="studio-map-strip"
      // Floating windows (the AI window) open below the strip, never over its buttons (useWindowManager).
      data-window-keep-clear=""
      data-together={together ? 'true' : 'false'}
      role="group"
      aria-label={t('mapView.label')}
      aria-live="polite"
    >
      {together ? (
        <Hint text={lines(`${t('mapView.together').replace('{name}', activeName)}\n${t('mapView.togetherHint')}`)}>
          <span className="flex min-w-0 items-center gap-1.5 text-ok" tabIndex={0} data-testid="map-view-together">
            <Play size={12} className="flex-none" aria-hidden="true" />
            <span className="sr-only">{togetherWords}</span>
            <span className="truncate">{activeName}</span>
          </span>
        </Hint>
      ) : (
        <>
          {viewing.length > 0 ? (
            <Hint text={lines(`${t('mapView.youSee').replace('{name}', viewing.map((id) => nameOf(id)).join(', '))}\n${ownHint}`)}>
              <span className="flex min-w-0 items-center gap-1.5 font-medium text-hl" tabIndex={0} data-testid="map-view-you">
                <Eye size={12} className="flex-none" aria-hidden="true" />
                <span className="sr-only">{youWords}</span>
                {viewing.map((id, i) => (
                  <span key={id} className={`truncate ${id === active ? 'text-ok' : ''}`} data-testid="map-view-you-name">
                    {nameOf(id)}
                    {i < viewing.length - 1 ? ',' : ''}
                  </span>
                ))}
              </span>
            </Hint>
          ) : null}
          <Hint text={t('mapView.playersSee').replace('{name}', activeName)}>
            <span className="flex min-w-0 items-center gap-1.5 text-fg" tabIndex={0} data-testid="map-view-players">
              <Play size={12} className="flex-none text-ok" aria-hidden="true" />
              <span className="sr-only">{playersWords}</span>
              <span className="truncate">{activeName}</span>
            </span>
          </Hint>
        </>
      )}
      {note !== undefined ? (
        <span className="min-w-0 truncate text-danger" title={note} data-testid="map-view-note">
          {note}
        </span>
      ) : null}
      <span className="flex flex-none items-center gap-0.5" data-testid="map-view-actions">
        {!viewing.includes(active) ? (
          <IconButton
            size="sm"
            label={t('mapView.openPlayersMap')}
            icon={<MapIcon size={14} aria-hidden="true" />}
            data-testid="map-view-return"
            onClick={onOpenPlayersMap}
          />
        ) : null}
        {showable.length === 1 ? (
          <IconButton
            size="sm"
            tone="accent"
            label={showLabel}
            hint={`${showLabel} — ${nameOf(showable[0]!) ?? ''}`}
            icon={<Cast size={14} aria-hidden="true" />}
            data-testid="map-view-show"
            disabled={pending}
            onClick={() => flow.ask(showable[0]!)}
          />
        ) : showable.length > 1 ? (
          // Two maps open: which one — words in the menu, then the same confirmation.
          <RowMenu
            label={showLabel}
            testid="map-view-show"
            icon={<Cast size={14} aria-hidden="true" />}
            items={showable.map((id) => ({
              label: `${nameOf(id) ?? ''}…`,
              icon: Cast,
              disabled: pending,
              testid: `map-view-show-${id}`,
              onSelect: () => flow.ask(id),
            }))}
          />
        ) : null}
      </span>
      <StripShowConfirm flow={flow} nameOf={nameOf} />
    </div>
  );
}

export default StudioMapStrip;
